import React, { useState, useEffect } from 'react'
import { useParams } from 'react-router-dom'
import {
  Container, Grid, Card, CardMedia,
  Dialog, Typography, Button, Box, IconButton,
} from '@mui/material'
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import CloseIcon from '@mui/icons-material/Close'
import { getHouse } from './api'

function toEmbedUrl(url) {
  if (!url) return null

  try {
    const parsed = new URL(url)
    const host = parsed.hostname.replace(/^www\./, '')

    if (host === 'youtu.be') {
      const id = parsed.pathname.split('/').filter(Boolean)[0]
      return id ? `https://www.youtube.com/embed/${id}` : null
    }

    if (host.endsWith('youtube.com')) {
      const videoId = parsed.searchParams.get('v')
      if (videoId) return `https://www.youtube.com/embed/${videoId}`

      const parts = parsed.pathname.split('/').filter(Boolean)
      const marker = parts.findIndex(part => part === 'embed' || part === 'shorts')
      if (marker !== -1 && parts[marker + 1]) {
        return `https://www.youtube.com/embed/${parts[marker + 1]}`
      }
    }
  } catch {
    return null
  }

  return null
}

function getEmbedUrls(house) {
  const sourceUrls = Array.isArray(house.youtube_urls) && house.youtube_urls.length
    ? house.youtube_urls
    : (house.youtube_url ? [house.youtube_url] : [])

  return sourceUrls
    .map(toEmbedUrl)
    .filter(Boolean)
}

function HouseDetail() {
  const { id } = useParams()
  const [house, setHouse] = useState(null)
  const [loading, setLoading] = useState(true)
  const [selectedImageIndex, setSelectedImageIndex] = useState(-1)

  useEffect(() => {
    getHouse(id)
      .then(setHouse)
      .finally(() => setLoading(false))
  }, [id])

  const images = Array.isArray(house?.images) ? house.images : []
  const embedUrls = house ? getEmbedUrls(house) : []
  const hasImages = images.length > 0
  const selectedImage = selectedImageIndex >= 0 ? images[selectedImageIndex] : null

  const openImage = (index) => setSelectedImageIndex(index)
  const closeImage = () => setSelectedImageIndex(-1)
  const showPrevImage = () => {
    if (!hasImages) return
    setSelectedImageIndex((currentIndex) => (
      (currentIndex - 1 + images.length) % images.length
    ))
  }
  const showNextImage = () => {
    if (!hasImages) return
    setSelectedImageIndex((currentIndex) => (
      (currentIndex + 1) % images.length
    ))
  }

  useEffect(() => {
    if (!selectedImage) return undefined

    const handleKeyDown = (event) => {
      if (event.key === 'ArrowLeft') showPrevImage()
      if (event.key === 'ArrowRight') showNextImage()
      if (event.key === 'Escape') closeImage()
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [selectedImage, images.length])

  if (loading) {
    return (
      <div style={{ padding: '80px', textAlign: 'center' }}>
        <Typography variant="h6" color="textSecondary">Loading...</Typography>
      </div>
    )
  }

  if (!house) {
    return (
      <div style={{ padding: '80px', textAlign: 'center' }}>
        <Typography variant="h6" color="textSecondary">House not found.</Typography>
      </div>
    )
  }

  return (
    <div style={{ paddingBottom: '40px' }}>
      <Container maxWidth="lg" style={{ padding: '32px 20px 16px' }}>
        <Typography variant="h4" gutterBottom>{house.name}</Typography>
        {house.location && (
          <Typography variant="subtitle1" color="textSecondary" gutterBottom>
            {house.location}
          </Typography>
        )}
        {house.description && (
          <Typography variant="body1" style={{ margin: '12px 0' }}>
            {house.description}
          </Typography>
        )}
        <Box mt={1} display="flex" gap={2} flexWrap="wrap">
          {house.zillow_url && (
            <Button
              variant="outlined"
              size="small"
              href={house.zillow_url}
              target="_blank"
              rel="noopener noreferrer"
            >
              View on Zillow
            </Button>
          )}
        </Box>

        {embedUrls.map((embedUrl, index) => (
          <Box
            key={embedUrl}
            mt={index === 0 ? 3 : 2}
            sx={{ position: 'relative', paddingTop: '56.25%', borderRadius: 2, overflow: 'hidden' }}
          >
            <iframe
              src={embedUrl}
              title={`House video ${index + 1}`}
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }}
            />
          </Box>
        ))}
      </Container>

      <Container maxWidth="lg">
        <Grid container spacing={2}>
          {images.map((img, index) => (
            <Grid item xs={12} sm={6} md={4} key={img.id}>
              <Card sx={{ backgroundColor: '#111', overflow: 'hidden' }}>
                <CardMedia
                  component="img"
                  height="220"
                  image={img.url}
                  alt={img.filename}
                  loading="lazy"
                  onClick={() => openImage(index)}
                  style={{ cursor: 'pointer', objectFit: 'cover', display: 'block' }}
                />
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>

      <Dialog open={!!selectedImage} onClose={closeImage} maxWidth="lg">
        {selectedImage && (
          <Box sx={{ position: 'relative', backgroundColor: '#0d0d0d' }}>
            <IconButton
              onClick={closeImage}
              sx={{
                position: 'absolute',
                top: 12,
                right: 12,
                zIndex: 2,
                color: '#fff',
                backgroundColor: 'rgba(0,0,0,0.45)',
                '&:hover': { backgroundColor: 'rgba(0,0,0,0.7)' },
              }}
            >
              <CloseIcon />
            </IconButton>
            {images.length > 1 && (
              <>
                <IconButton
                  onClick={showPrevImage}
                  sx={{
                    position: 'absolute',
                    top: '50%',
                    left: 12,
                    transform: 'translateY(-50%)',
                    zIndex: 2,
                    color: '#fff',
                    backgroundColor: 'rgba(0,0,0,0.45)',
                    '&:hover': { backgroundColor: 'rgba(0,0,0,0.7)' },
                  }}
                >
                  <ChevronLeftIcon />
                </IconButton>
                <IconButton
                  onClick={showNextImage}
                  sx={{
                    position: 'absolute',
                    top: '50%',
                    right: 12,
                    transform: 'translateY(-50%)',
                    zIndex: 2,
                    color: '#fff',
                    backgroundColor: 'rgba(0,0,0,0.45)',
                    '&:hover': { backgroundColor: 'rgba(0,0,0,0.7)' },
                  }}
                >
                  <ChevronRightIcon />
                </IconButton>
              </>
            )}
            <img
              src={selectedImage.url}
              alt={selectedImage.filename || house.name}
              style={{ width: '100%', height: 'auto', display: 'block', maxHeight: '88vh', objectFit: 'contain' }}
            />
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                px: 2,
                py: 1.5,
                color: '#f0f0f0',
              }}
            >
              <Typography variant="body2" sx={{ pr: 2 }}>
                {selectedImage.filename || house.name}
              </Typography>
              <Typography variant="caption" color="rgba(255,255,255,0.75)">
                {selectedImageIndex + 1} / {images.length}
              </Typography>
            </Box>
          </Box>
        )}
      </Dialog>
    </div>
  )
}

export default HouseDetail
